import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type {
  AplicarPagoInput,
  AplicarPagoResponse,
  AplicarSaldoFavorInput,
  AplicarSaldoFavorResponse,
  EstadoCuenta,
  FacturaResponse,
  PagedFacturas,
  PagedPagos,
} from "./schemas";

export type AnularFacturaInput = {
  factura_id: string;
  motivo: string;
};

export type FacturasFilters = {
  page?: number;
  size?: number;
  cliente_id?: string;
  contrato_id?: string;
  estatus?: string;
  desde?: string;  // YYYY-MM-DD
  hasta?: string;
};

function buildSearchParams(filters: FacturasFilters): Record<string, string | number> {
  const p: Record<string, string | number> = {
    page: filters.page ?? 1,
    size: filters.size ?? 50,
  };
  if (filters.cliente_id) p.cliente_id = filters.cliente_id;
  if (filters.contrato_id) p.contrato_id = filters.contrato_id;
  if (filters.estatus) p.estatus = filters.estatus;
  if (filters.desde) p.desde = filters.desde;
  if (filters.hasta) p.hasta = filters.hasta;
  return p;
}

export function useListFacturas(filters: FacturasFilters) {
  return useQuery({
    queryKey: ["facturas", filters],
    queryFn: () =>
      apiCall<PagedFacturas>("get", "/api/v1/facturas", {
        searchParams: buildSearchParams(filters),
      }),
    placeholderData: (prev) => prev,
  });
}

/** Lista facturas de un cliente — para el tab en ClienteDetailPage. */
export function useFacturasCliente(clienteId: string | null) {
  return useQuery({
    queryKey: ["facturas", "cliente", clienteId],
    queryFn: () =>
      apiCall<PagedFacturas>("get", "/api/v1/facturas", {
        searchParams: { cliente_id: clienteId!, size: 100 },
      }),
    enabled: !!clienteId,
    placeholderData: (prev) => prev,
  });
}

export function useFactura(id: string | null) {
  return useQuery({
    queryKey: ["factura", id],
    queryFn: () => apiCall<FacturaResponse>("get", `/api/v1/facturas/${id}`),
    enabled: !!id,
  });
}

/** Lista pagos aplicados a una factura. Incluye anulados para ver histórico. */
export function usePagosFactura(facturaId: string | null) {
  return useQuery({
    queryKey: ["pagos", "factura", facturaId],
    queryFn: () =>
      apiCall<PagedPagos>("get", "/api/v1/pagos", {
        searchParams: { factura_id: facturaId!, incluir_anulados: true, size: 100 },
      }),
    enabled: !!facturaId,
  });
}

export function useEstadoCuenta(clienteId: string | null) {
  return useQuery({
    queryKey: ["estado-cuenta", clienteId],
    queryFn: () =>
      apiCall<EstadoCuenta>("get", `/api/v1/clientes/${clienteId}/estado-cuenta`),
    enabled: !!clienteId,
  });
}

/**
 * Hook para anular una factura (requiere motivo).
 * Backend en cascada anula los pagos aplicados. El audit fan-out ya
 * registra la anulación en cliente, contrato y factura.
 */
export function useAnularFactura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ factura_id, motivo }: AnularFacturaInput) =>
      apiCall<FacturaResponse>(
        "post",
        `/api/v1/facturas/${factura_id}/anular`,
        { json: { motivo } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["facturas"] });
      qc.invalidateQueries({ queryKey: ["factura"] });
      qc.invalidateQueries({ queryKey: ["pagos"] });
      qc.invalidateQueries({ queryKey: ["estado-cuenta"] });
      qc.invalidateQueries({ queryKey: ["clientes", "resumen"] });
    },
  });
}

/**
 * Hook para aplicar pago. El backend devuelve pago + metadata (saldo,
 * estatus factura, contratos auto-reactivados) para feedback rico.
 *
 * Invalida cache de: facturas, pagos, estado_cuenta, contratos (por si hubo
 * auto-reactivación), clientes/resumen (strip en detalle del cliente).
 */
export function useAplicarPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AplicarPagoInput) =>
      apiCall<AplicarPagoResponse>("post", "/api/v1/pagos", { json: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["facturas"] });
      qc.invalidateQueries({ queryKey: ["factura"] });
      qc.invalidateQueries({ queryKey: ["pagos"] });
      qc.invalidateQueries({ queryKey: ["estado-cuenta"] });
      qc.invalidateQueries({ queryKey: ["contratos"] });
      qc.invalidateQueries({ queryKey: ["clientes", "resumen"] });
    },
  });
}

/**
 * Hook para aplicar el saldo a favor disponible del cliente a una factura
 * existente (rc68). Para casos donde el pago huérfano llegó después de emitida
 * la factura (SURPLUS Toku, migración). Invalida la misma cache que aplicar pago.
 */
export function useAplicarSaldoFavor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ factura_id, motivo }: AplicarSaldoFavorInput) =>
      apiCall<AplicarSaldoFavorResponse>(
        "post",
        `/api/v1/facturas/${factura_id}/aplicar-saldo-favor`,
        { json: { motivo: motivo ?? null } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["facturas"] });
      qc.invalidateQueries({ queryKey: ["factura"] });
      qc.invalidateQueries({ queryKey: ["pagos"] });
      qc.invalidateQueries({ queryKey: ["estado-cuenta"] });
      qc.invalidateQueries({ queryKey: ["contratos"] });
      qc.invalidateQueries({ queryKey: ["clientes", "resumen"] });
    },
  });
}

// ── Servicio adicional (rc44) ──────────────────────────────────────────────
/** Input pa POST /api/v1/facturas/servicio-adicional. Espejo de
 *  ServicioAdicionalCreate en backend. */
export type ServicioAdicionalInput = {
  contrato_id: string;
  monto: string;             // Decimal as string
  concepto: string;
  fecha_vencimiento: string; // ISO date YYYY-MM-DD
  notas?: string | null;
};

/** Hook pa emitir Servicio adicional (factura tipo libre).
 *
 *  Backend requiere permiso `facturas.libre.create` (solo admin).
 *  No cuenta para el poller de cobranza — útil pa cargos extras tipo
 *  cambio de domicilio, equipo extra, reinstalación, etc.
 */
export function useEmitirServicioAdicional() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ServicioAdicionalInput) =>
      apiCall<FacturaResponse>("post", "/api/v1/facturas/servicio-adicional", {
        json: data,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["facturas"] });
      qc.invalidateQueries({ queryKey: ["estado-cuenta"] });
      qc.invalidateQueries({ queryKey: ["clientes", "resumen"] });
    },
  });
}
